import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Upload, X, Plus, Check, Loader2, AlertTriangle, CheckCircle2, BookOpen, Sparkles,
  Trash2, Search, Star, ChevronLeft, Save, Edit2, Layers, RefreshCw,
} from 'lucide-react';
import { getCurrentUser } from '../services/auth';
import { neonQuery } from '../services/api';
import { BRANDS as SHARED_BRANDS } from '../lib/brands';
import {
  ORIGINAL_MAX_EDGE, THUMB_MAX_EDGE, ORIGINAL_QUALITY, THUMB_QUALITY,
  isVideoFile, isHeicFile, isMediaFile, isGifFile, GIF_RAW_MAX_BYTES, normalizeTag,
  fileToResizedBase64, fileToRawBase64, fileToUploadOriginal, fileImageDims, sha256Hex, videoPoster, thumbUrl,
} from '../lib/mediaUpload';

/**
 * Media Intake — the staging area and training tool for the Media Library.
 *
 * Three tabs, in the order you actually use them:
 *   Stage      drop a folder with a brief, review what the AI made of it, commit
 *   Sweeps     approve the matches it found in the EXISTING library
 *   Vernacular the house dictionary that teaches all of the above our words
 *
 * The Vernacular tab is the part that compounds. Everything else is a nicer
 * upload screen; the dictionary is what stops us relearning our own vocabulary
 * every time someone new uploads a folder.
 */

const INTAKE_FN = '/.netlify/functions/media-intake';
const BRANDS = SHARED_BRANDS.map((b) => ({ id: b.value, label: b.label }));
const CATEGORIES = ['products', 'plated', 'lifestyle', 'ugc', 'process', 'people', 'marketing', 'other'];

async function intakePost(payload) {
  const res = await fetch(INTAKE_FN, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `Request failed (${res.status})`);
  return data;
}

// ── Shared bits ──────────────────────────────────────────────────────────────
const card = { background: '#fff', border: '1px solid var(--gray-200)', borderRadius: 8 };
const label = { fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, color: 'var(--gray-500)' };
const input = { width: '100%', padding: '7px 9px', fontSize: 13, border: '1px solid var(--gray-200)', borderRadius: 4, fontFamily: 'inherit', color: '#1d1d1f', boxSizing: 'border-box' };
const btnPrimary = { padding: '7px 14px', fontSize: 13, background: 'var(--orange)', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontWeight: 600 };
const btnGhost = { padding: '7px 14px', fontSize: 13, background: '#fff', color: 'var(--navy)', border: '1px solid var(--gray-200)', borderRadius: 4, cursor: 'pointer', fontWeight: 600 };

function TagChips({ tags, onRemove, tone = 'orange' }) {
  const styles = tone === 'orange'
    ? { background: '#fff4e6', color: '#7a3500', border: '1px solid #ffd6a3' }
    : { background: '#eef4fa', color: 'var(--navy)', border: '1px solid #cfe0ef' };
  if (!tags || !tags.length) return null;
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
      {tags.map((t) => (
        <span key={t} style={{ ...styles, padding: onRemove ? '2px 4px 2px 8px' : '2px 8px', borderRadius: 3, fontSize: 11, display: 'inline-flex', alignItems: 'center', gap: 2 }}>
          {t}
          {onRemove ? (
            <button onClick={() => onRemove(t)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 1, display: 'inline-flex', color: 'inherit' }}>
              <X size={11} />
            </button>
          ) : null}
        </span>
      ))}
    </div>
  );
}

function TagInput({ tags, setTags, placeholder }) {
  const [text, setText] = useState('');
  const commit = () => {
    const t = normalizeTag(text);
    setText('');
    if (t && !tags.includes(t)) setTags([...tags, t]);
  };
  return (
    <div>
      <div style={{ display: 'flex', gap: 6 }}>
        <input
          style={input}
          value={text}
          placeholder={placeholder || 'add a tag and press Enter'}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); commit(); } }}
          onBlur={commit}
        />
        <button type="button" onClick={commit} style={{ ...btnPrimary, padding: '7px 10px' }}><Plus size={13} /></button>
      </div>
      {tags.length ? <div style={{ marginTop: 6 }}><TagChips tags={tags} onRemove={(t) => setTags(tags.filter((x) => x !== t))} /></div> : null}
    </div>
  );
}

function Stars({ value, onChange, size = 13 }) {
  return (
    <span style={{ display: 'inline-flex', gap: 1 }}>
      {[1, 2, 3, 4, 5].map((n) => (
        <Star
          key={n}
          size={size}
          onClick={onChange ? (e) => { e.stopPropagation(); onChange(n); } : undefined}
          style={{ cursor: onChange ? 'pointer' : 'default', color: n <= (value || 0) ? '#f0a020' : 'var(--gray-200)' }}
          fill={n <= (value || 0) ? '#f0a020' : 'none'}
        />
      ))}
    </span>
  );
}

function Empty({ icon, title, body }) {
  return (
    <div style={{ ...card, padding: '38px 24px', textAlign: 'center', color: 'var(--gray-500)' }}>
      <div style={{ marginBottom: 10, display: 'flex', justifyContent: 'center' }}>{icon}</div>
      <div style={{ fontWeight: 600, color: 'var(--navy)', fontSize: 14, marginBottom: 4 }}>{title}</div>
      <div style={{ fontSize: 12, maxWidth: 520, margin: '0 auto', lineHeight: 1.6 }}>{body}</div>
    </div>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────
export default function MediaIntake() {
  const user = getCurrentUser();
  const roles = Array.isArray(user?.roles) ? user.roles : (user?.roles || '').split(',').map((r) => r.trim());
  const allowed = roles.includes('Admin') || roles.includes('Manager') || roles.includes('Marketing');
  const [tab, setTab] = useState('stage');
  // Set when "Add to Vernacular" is pressed on a batch — carries the batch's
  // brief across to the dictionary form so teaching a term is one click, not
  // a retyping exercise.
  const [vocabSeed, setVocabSeed] = useState(null);
  const [sweepJump, setSweepJump] = useState(null);

  if (!allowed) {
    return (
      <div style={{ padding: 24 }}>
        <h1 style={{ fontSize: 20, color: 'var(--navy)' }}>Media Intake</h1>
        <p style={{ fontSize: 13, color: 'var(--gray-500)' }}>You need the Admin, Manager or Marketing role to use this page.</p>
      </div>
    );
  }

  const TABS = [
    { id: 'stage', label: 'Stage', icon: <Upload size={13} /> },
    { id: 'sweeps', label: 'Sweeps', icon: <Sparkles size={13} /> },
    { id: 'vernacular', label: 'Vernacular', icon: <BookOpen size={13} /> },
  ];

  return (
    <div style={{ padding: 20, maxWidth: 1400, margin: '0 auto' }}>
      <div style={{ marginBottom: 4, fontSize: 12, color: 'var(--gray-500)' }}>Marketing &rsaquo; Media Intake</div>
      <h1 style={{ fontSize: 22, color: 'var(--navy)', margin: '0 0 6px' }}>Media Intake</h1>
      <p style={{ fontSize: 13, color: 'var(--gray-500)', maxWidth: 860, lineHeight: 1.6, margin: '0 0 16px' }}>
        Stage a folder with a description before it reaches the library, then teach the library what
        that description means so everything already in there gets filed the same way.
      </p>

      <div style={{ display: 'flex', gap: 6, marginBottom: 16, borderBottom: '1px solid var(--gray-200)' }}>
        {TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            style={{
              padding: '8px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer',
              background: 'none', border: 'none', borderBottom: tab === t.id ? '2px solid var(--orange)' : '2px solid transparent',
              color: tab === t.id ? 'var(--navy)' : 'var(--gray-500)', display: 'inline-flex', alignItems: 'center', gap: 6,
              marginBottom: -1,
            }}
          >
            {t.icon}{t.label}
          </button>
        ))}
      </div>

      {tab === 'stage' ? (
        <StageTab
          user={user}
          onTeach={(seed) => { setVocabSeed(seed); setTab('vernacular'); }}
          onSweepStarted={(id) => { setSweepJump(id); setTab('sweeps'); }}
        />
      ) : null}
      {tab === 'sweeps' ? <SweepsTab user={user} jumpTo={sweepJump} onConsumeJump={() => setSweepJump(null)} /> : null}
      {tab === 'vernacular' ? <VernacularTab user={user} seed={vocabSeed} onConsumeSeed={() => setVocabSeed(null)} /> : null}
    </div>
  );
}

// ── Stage tab ────────────────────────────────────────────────────────────────
function StageTab({ user, onTeach, onSweepStarted }) {
  const [batches, setBatches] = useState([]);
  const [openId, setOpenId] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadBatches = useCallback(async () => {
    setLoading(true);
    try { setBatches(await neonQuery('media_batches_list', { limit: 40 }) || []); }
    catch (e) { setBatches([]); }
    setLoading(false);
  }, []);

  useEffect(() => { loadBatches(); }, [loadBatches]);

  if (openId) {
    return (
      <BatchDetail
        batchId={openId}
        user={user}
        onBack={() => { setOpenId(null); loadBatches(); }}
        onTeach={onTeach}
        onSweepStarted={onSweepStarted}
      />
    );
  }

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 380px) minmax(0, 1fr)', gap: 16, alignItems: 'start' }}>
      <NewBatchForm user={user} onCreated={(b) => { loadBatches(); setOpenId(b.id); }} />
      <div>
        <div style={{ ...label, marginBottom: 8 }}>Recent batches</div>
        {loading ? (
          <div style={{ ...card, padding: 20, fontSize: 13, color: 'var(--gray-500)' }}>Loading…</div>
        ) : !batches.length ? (
          <Empty
            icon={<Layers size={26} color="var(--gray-500)" />}
            title="No batches yet"
            body="Start one on the left. Give it a name, say what the photos are in plain English, and list the tags you want to find them by later."
          />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {batches.map((b) => (
              <button
                key={b.id}
                onClick={() => setOpenId(b.id)}
                style={{ ...card, padding: 12, textAlign: 'left', cursor: 'pointer', display: 'block', width: '100%' }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                  <span style={{ fontWeight: 600, color: 'var(--navy)', fontSize: 14 }}>{b.name}</span>
                  <span style={{
                    fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, padding: '2px 6px', borderRadius: 3,
                    background: b.status === 'committed' ? '#e8f5ec' : '#fff4e6',
                    color: b.status === 'committed' ? '#1a7f37' : '#7a3500',
                  }}>{b.status === 'committed' ? 'Committed' : 'Staging'}</span>
                  <span style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--gray-500)' }}>
                    {b.item_count} file{b.item_count === 1 ? '' : 's'}
                    {b.duplicate_count ? ` · ${b.duplicate_count} already in library` : ''}
                  </span>
                </div>
                {b.brief ? <div style={{ fontSize: 12, color: 'var(--gray-500)', lineHeight: 1.5, marginBottom: 6 }}>{b.brief}</div> : null}
                <TagChips tags={b.target_tags} />
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function NewBatchForm({ user, onCreated }) {
  const [name, setName] = useState('');
  const [brief, setBrief] = useState('');
  const [tags, setTags] = useState([]);
  const [brand, setBrand] = useState('crab-place');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const create = async () => {
    if (!name.trim()) { setErr('Give the batch a name.'); return; }
    setSaving(true); setErr('');
    try {
      const rows = await neonQuery('media_batch_create', {
        name, brief: brief.trim() || null, target_tags: tags, brand,
        admin_id: user?.id, admin_name: user?.staffName,
      });
      if (rows && rows[0]) onCreated(rows[0]);
    } catch (e) { setErr(e.message); }
    setSaving(false);
  };

  return (
    <div style={{ ...card, padding: 14 }}>
      <div style={{ fontWeight: 600, color: 'var(--navy)', fontSize: 14, marginBottom: 12 }}>New batch</div>

      <div style={{ marginBottom: 12 }}>
        <div style={{ ...label, marginBottom: 4 }}>Name</div>
        <input style={input} value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Plated lobster tails" />
      </div>

      <div style={{ marginBottom: 12 }}>
        <div style={{ ...label, marginBottom: 4 }}>What are these? Why are we adding them?</div>
        <textarea
          style={{ ...input, minHeight: 84, resize: 'vertical' }}
          value={brief}
          onChange={(e) => setBrief(e.target.value)}
          placeholder="Plain English. e.g. Plated lobster tails — cooked tails on a plate, styled for eating, for recipe pages and the lobster PDP. Not raw tails on ice."
        />
        <div style={{ fontSize: 11, color: 'var(--gray-500)', marginTop: 4, lineHeight: 1.5 }}>
          This is read by Claude as it classifies each file, and it is also what a library sweep
          searches on later. Saying what it is <em>not</em> helps as much as saying what it is.
        </div>
      </div>

      <div style={{ marginBottom: 12 }}>
        <div style={{ ...label, marginBottom: 4 }}>Tags to apply</div>
        <TagInput tags={tags} setTags={setTags} placeholder="e.g. plated-lobster" />
      </div>

      <div style={{ marginBottom: 14 }}>
        <div style={{ ...label, marginBottom: 4 }}>Brand</div>
        <div style={{ display: 'flex', gap: 6 }}>
          {BRANDS.map((b) => (
            <button
              key={b.id}
              onClick={() => setBrand(b.id)}
              style={{
                padding: '6px 12px', fontSize: 12, borderRadius: 4, cursor: 'pointer', fontWeight: 600,
                background: brand === b.id ? 'var(--navy)' : '#fff',
                color: brand === b.id ? '#fff' : 'var(--navy)',
                border: `1px solid ${brand === b.id ? 'var(--navy)' : 'var(--gray-200)'}`,
              }}
            >{b.label}</button>
          ))}
        </div>
      </div>

      {err ? <div style={{ fontSize: 12, color: '#c33', marginBottom: 10 }}>{err}</div> : null}
      <button onClick={create} disabled={saving} style={{ ...btnPrimary, width: '100%', opacity: saving ? 0.6 : 1 }}>
        {saving ? 'Creating…' : 'Create batch and add files'}
      </button>
    </div>
  );
}

// ── Batch detail: drop, review, commit ───────────────────────────────────────
function BatchDetail({ batchId, user, onBack, onTeach, onSweepStarted }) {
  const [batch, setBatch] = useState(null);
  const [items, setItems] = useState([]);
  const [queue, setQueue] = useState([]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const [showSweep, setShowSweep] = useState(false);
  const fileRef = useRef(null);
  const folderRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const rows = await neonQuery('media_batch_get', { id: batchId });
      if (rows && rows[0]) { setBatch(rows[0].batch); setItems(rows[0].items || []); }
    } catch (e) { setErr(e.message); }
  }, [batchId]);

  useEffect(() => { load(); }, [load]);

  const stageFiles = useCallback(async (fileList) => {
    const files = Array.from(fileList || []).filter((f) => isMediaFile(f) && f.size > 0);
    if (!files.length) return;
    setBusy(true); setErr('');
    setQueue(files.map((f, i) => ({ id: `${f.name}-${i}-${f.size}`, name: f.name, status: 'queued' })));

    // Sequential on purpose: each file costs a vision call, and hammering the
    // API in parallel from the browser is how you get 429s mid-folder.
    for (let i = 0; i < files.length; i++) {
      const f = files[i];
      const key = `${f.name}-${i}-${f.size}`;
      setQueue((q) => q.map((x) => (x.id === key ? { ...x, status: 'working' } : x)));
      try {
        let data;
        if (isVideoFile(f)) {
          const sha = await sha256Hex(f);
          const ext = (f.name.split('.').pop() || 'mp4').toLowerCase();
          const { uploadUrl, key: r2key, contentType } = await intakePost({ action: 'presign', sha, ext });
          await fetch(uploadUrl, { method: 'PUT', headers: { 'Content-Type': contentType }, body: f });
          let poster = {};
          try { poster = await videoPoster(f); } catch (e) { poster = {}; }
          data = await intakePost({ action: 'stage-video', batchId, sha, key: r2key, filename: f.name, ...poster });
        } else if (isHeicFile(f)) {
          const originalBase64 = await fileToRawBase64(f);
          data = await intakePost({ action: 'stage', batchId, originalBase64, filename: f.name, contentType: 'image/heic' });
        } else if (isGifFile(f) && f.size <= GIF_RAW_MAX_BYTES) {
          // Raw bytes so animation + transparency survive (the canvas path
          // flattens to one JPEG frame). Oversized GIFs fall through below.
          const [originalBase64, dims] = await Promise.all([fileToRawBase64(f), fileImageDims(f)]);
          data = await intakePost({
            action: 'stage', batchId, originalBase64, filename: f.name, contentType: 'image/gif',
            width: dims.width, height: dims.height,
          });
        } else {
          // Alpha-aware: a PNG/WebP with real transparency (brand logos) stays
          // PNG so the transparency survives; everything else ships JPEG. The
          // server discards the JPEG thumb for transparent files and makes its
          // own PNG thumb.
          const [orig, thumb] = await Promise.all([
            fileToUploadOriginal(f, ORIGINAL_MAX_EDGE, ORIGINAL_QUALITY),
            fileToResizedBase64(f, THUMB_MAX_EDGE, THUMB_QUALITY),
          ]);
          const isPng = orig.format === 'png';
          data = await intakePost({
            action: 'stage', batchId, originalBase64: orig.base64, thumbBase64: thumb.base64,
            filename: isPng ? f.name.replace(/\.[a-z0-9]+$/i, '.png') : f.name,
            contentType: isPng ? 'image/png' : 'image/jpeg', width: orig.naturalWidth, height: orig.naturalHeight,
          });
        }
        if (data.item) {
          setItems((prev) => {
            const without = prev.filter((p) => p.id !== data.item.id);
            return [data.item, ...without];
          });
        }
        setQueue((q) => q.map((x) => (x.id === key ? { ...x, status: 'done', note: data.duplicate ? 'already in library' : (data.matchesBrief === false ? 'flagged' : '') } : x)));
      } catch (e) {
        setQueue((q) => q.map((x) => (x.id === key ? { ...x, status: 'error', note: e.message } : x)));
      }
    }
    setBusy(false);
    load();
  }, [batchId, load]);

  const setDecision = async (ids, decision) => {
    setItems((prev) => prev.map((it) => (ids.includes(it.id) ? { ...it, decision } : it)));
    try { await neonQuery('media_batch_bulk_decision', { ids, decision }); } catch (e) { setErr(e.message); }
  };

  const patchItem = async (id, fields) => {
    setItems((prev) => prev.map((it) => (it.id === id ? { ...it, ...fields } : it)));
    try { await neonQuery('media_batch_item_update', { id, fields }); } catch (e) { setErr(e.message); }
  };

  const commit = async () => {
    setBusy(true); setErr(''); setMsg('');
    try {
      const r = await intakePost({ action: 'commit', batchId, admin_id: user?.id });
      setMsg(`Committed — ${r.created} added to the library${r.merged ? `, ${r.merged} already there and re-tagged` : ''}.`);
      await load();
      setShowSweep(true);
    } catch (e) { setErr(e.message); }
    setBusy(false);
  };

  if (!batch) return <div style={{ ...card, padding: 20, fontSize: 13, color: 'var(--gray-500)' }}>Loading…</div>;

  const approved = items.filter((i) => i.decision === 'approved' && !i.asset_id).length;
  const flagged = items.filter((i) => (i.notes || '').startsWith('Does not match the batch brief'));
  const committed = batch.status === 'committed';

  return (
    <div>
      <button onClick={onBack} style={{ ...btnGhost, marginBottom: 12, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
        <ChevronLeft size={13} /> All batches
      </button>

      <div style={{ ...card, padding: 14, marginBottom: 14 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 260 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <h2 style={{ fontSize: 17, color: 'var(--navy)', margin: 0 }}>{batch.name}</h2>
              {committed ? (
                <span style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, padding: '2px 6px', borderRadius: 3, background: '#e8f5ec', color: '#1a7f37' }}>Committed</span>
              ) : null}
            </div>
            {batch.brief ? <div style={{ fontSize: 12.5, color: 'var(--gray-500)', lineHeight: 1.6, marginTop: 6 }}>{batch.brief}</div> : null}
            <div style={{ marginTop: 8 }}><TagChips tags={batch.target_tags} /></div>
          </div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            <button
              onClick={() => onTeach({ term: (batch.target_tags || [])[0] || '', label: batch.name, definition: batch.brief || '', category: (items[0] || {}).category || '', subcategory: (items[0] || {}).subcategory || '' })}
              style={{ ...btnGhost, display: 'inline-flex', alignItems: 'center', gap: 6 }}
              title="Turn this batch's description into a permanent house term"
            >
              <BookOpen size={13} /> Add to Vernacular
            </button>
            {committed ? (
              <button onClick={() => setShowSweep(true)} style={{ ...btnPrimary, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <Sparkles size={13} /> Find more in the library
              </button>
            ) : null}
          </div>
        </div>
      </div>

      {!committed ? (
        <div
          onDragOver={(e) => { e.preventDefault(); }}
          onDrop={(e) => { e.preventDefault(); stageFiles(e.dataTransfer.files); }}
          style={{ ...card, padding: 16, marginBottom: 14, borderStyle: 'dashed', borderColor: busy ? 'var(--orange)' : 'var(--gray-200)', background: '#fafafa' }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <Upload size={16} color="var(--gray-500)" />
            <button onClick={() => fileRef.current?.click()} disabled={busy} style={btnPrimary}>Choose files…</button>
            <button onClick={() => folderRef.current?.click()} disabled={busy} style={btnGhost}>Choose folder…</button>
            <span style={{ fontSize: 12, color: 'var(--gray-500)' }}>or drag a folder onto this box. Images and video. Nothing reaches the library until you commit.</span>
          </div>
          <input ref={fileRef} type="file" multiple accept="image/*,video/*,.heic,.heif" style={{ display: 'none' }} onChange={(e) => { stageFiles(e.target.files); e.target.value = ''; }} />
          <input ref={folderRef} type="file" webkitdirectory="" directory="" multiple style={{ display: 'none' }} onChange={(e) => { stageFiles(e.target.files); e.target.value = ''; }} />

          {queue.length ? (
            <div style={{ marginTop: 12, maxHeight: 160, overflowY: 'auto', borderTop: '1px solid var(--gray-200)', paddingTop: 8 }}>
              {queue.map((q) => (
                <div key={q.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, padding: '3px 0' }}>
                  {q.status === 'working' ? <Loader2 size={12} style={{ animation: 'spin 1s linear infinite', color: 'var(--orange)' }} />
                    : q.status === 'done' ? <CheckCircle2 size={12} color="#1a7f37" />
                    : q.status === 'error' ? <AlertTriangle size={12} color="#c33" />
                    : <Loader2 size={12} style={{ opacity: 0.3 }} />}
                  <span style={{ color: 'var(--navy)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{q.name}</span>
                  {q.note ? <span style={{ color: q.status === 'error' ? '#c33' : 'var(--gray-500)', fontSize: 11 }}>{q.note}</span> : null}
                </div>
              ))}
            </div>
          ) : null}
        </div>
      ) : null}

      {msg ? <div style={{ ...card, padding: '10px 12px', marginBottom: 12, background: '#e8f5ec', borderColor: '#b7e0c2', color: '#1a7f37', fontSize: 13 }}>{msg}</div> : null}
      {err ? <div style={{ ...card, padding: '10px 12px', marginBottom: 12, background: '#fdecea', borderColor: '#f5c2c0', color: '#c33', fontSize: 13 }}>{err}</div> : null}

      {flagged.length && !committed ? (
        <div style={{ ...card, padding: '10px 12px', marginBottom: 12, background: '#fff8e6', borderColor: '#ffd98a', fontSize: 13, color: '#7a5200', display: 'flex', alignItems: 'center', gap: 8 }}>
          <AlertTriangle size={15} />
          <span style={{ flex: 1 }}>
            {flagged.length} file{flagged.length === 1 ? " doesn't" : "s don't"} look like the rest of this batch. They're at the top — check them, then reject or keep.
          </span>
          <button onClick={() => setDecision(flagged.map((f) => f.id), 'rejected')} style={{ ...btnGhost, padding: '5px 10px', fontSize: 12 }}>Reject all flagged</button>
        </div>
      ) : null}

      {items.length ? (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
            <div style={{ fontSize: 13, color: 'var(--navy)', fontWeight: 600 }}>
              {items.length} staged · {approved} approved
            </div>
            {!committed ? (
              <>
                <button onClick={() => setDecision(items.map((i) => i.id), 'approved')} style={{ ...btnGhost, padding: '5px 10px', fontSize: 12 }}>Approve all</button>
                <button
                  onClick={commit}
                  disabled={busy || !approved}
                  style={{ ...btnPrimary, marginLeft: 'auto', opacity: busy || !approved ? 0.5 : 1, display: 'inline-flex', alignItems: 'center', gap: 6 }}
                >
                  <Check size={13} /> Commit {approved} to the library
                </button>
              </>
            ) : null}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 10 }}>
            {items.map((it) => (
              <StagedCard key={it.id} item={it} locked={committed} onPatch={patchItem} onDecision={(d) => setDecision([it.id], d)} />
            ))}
          </div>
        </>
      ) : !busy ? (
        <Empty
          icon={<Upload size={26} color="var(--gray-500)" />}
          title="Nothing staged yet"
          body="Drop the folder above. Every file still gets scanned, named, star-rated and tagged automatically — the brief just tells Claude what it's looking at, and it will flag anything that doesn't belong."
        />
      ) : null}

      {showSweep ? (
        <SweepDialog
          batch={batch}
          user={user}
          onClose={() => setShowSweep(false)}
          onStarted={(id) => { setShowSweep(false); onSweepStarted(id); }}
        />
      ) : null}
    </div>
  );
}

function StagedCard({ item, locked, onPatch, onDecision }) {
  const flagged = (item.notes || '').startsWith('Does not match the batch brief');
  const rejected = item.decision === 'rejected';
  return (
    <div style={{
      ...card, overflow: 'hidden', opacity: rejected ? 0.45 : 1,
      borderColor: flagged ? '#ffd98a' : 'var(--gray-200)',
      borderWidth: flagged ? 2 : 1,
    }}>
      <div style={{ position: 'relative', background: '#f4f4f6', aspectRatio: '4 / 3' }}>
        <img src={thumbUrl(item.r2_thumb_key)} alt={item.alt_text || ''} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
        {item.duplicate_asset_id ? (
          <span style={{ position: 'absolute', top: 6, left: 6, background: 'rgba(0,57,92,0.88)', color: '#fff', fontSize: 10, fontWeight: 700, padding: '2px 6px', borderRadius: 3 }}>
            ALREADY IN LIBRARY
          </span>
        ) : null}
        {item.media_kind === 'video' ? (
          <span style={{ position: 'absolute', top: 6, right: 6, background: 'rgba(0,0,0,0.7)', color: '#fff', fontSize: 10, fontWeight: 700, padding: '2px 6px', borderRadius: 3 }}>VIDEO</span>
        ) : null}
      </div>

      <div style={{ padding: 10 }}>
        {flagged ? (
          <div style={{ fontSize: 11, color: '#7a5200', background: '#fff8e6', border: '1px solid #ffd98a', borderRadius: 3, padding: '4px 6px', marginBottom: 6, lineHeight: 1.4 }}>
            {item.notes}
          </div>
        ) : null}
        <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--navy)', wordBreak: 'break-word', marginBottom: 3 }}>{item.suggested_name}</div>
        <div style={{ fontSize: 11, color: 'var(--gray-500)', marginBottom: 6 }}>{item.category}/{item.subcategory}</div>
        <div style={{ marginBottom: 6 }}>
          <Stars value={item.quality_score} onChange={locked ? undefined : (n) => onPatch(item.id, { quality_score: n })} />
        </div>
        <TagChips tags={item.tags} tone="navy" />
        {!locked ? (
          <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
            <button
              onClick={() => onDecision(rejected ? 'approved' : 'rejected')}
              style={{
                flex: 1, padding: '5px 8px', fontSize: 11, borderRadius: 4, cursor: 'pointer', fontWeight: 600,
                background: rejected ? 'var(--navy)' : '#fff',
                color: rejected ? '#fff' : '#c33',
                border: `1px solid ${rejected ? 'var(--navy)' : '#f0c4c2'}`,
              }}
            >
              {rejected ? 'Keep it' : "Don't add"}
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
}

function SweepDialog({ batch, user, onClose, onStarted }) {
  // NOTE: do not name this state "label" — that shadows the module-level `label`
  // style object, and `{ ...label }` then spreads a STRING into style={}, which
  // throws in Chrome (indexed CSSStyleDeclaration setter) and blanks the whole app.
  const [sweepName, setSweepName] = useState(batch.name);
  const [brief, setBrief] = useState(batch.brief || '');
  const [tags, setTags] = useState(batch.target_tags || []);
  const [cap, setCap] = useState(400);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const go = async () => {
    if (!tags.length) { setErr('Pick at least one tag to apply.'); return; }
    setBusy(true); setErr('');
    try {
      const r = await intakePost({
        action: 'start-sweep', batchId: batch.id, label: sweepName, brief, applyTags: tags,
        brand: batch.brand, poolCap: cap, admin_id: user?.id,
      });
      onStarted(r.sweep.id);
    } catch (e) { setErr(e.message); setBusy(false); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 900, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div style={{ ...card, width: 'min(560px, 100%)', maxHeight: '90vh', overflowY: 'auto' }}>
        <div style={{ padding: '12px 14px', borderBottom: '1px solid var(--gray-200)', display: 'flex', alignItems: 'center', gap: 8 }}>
          <Sparkles size={15} color="var(--orange)" />
          <span style={{ fontWeight: 600, color: 'var(--navy)', fontSize: 14 }}>Find more like this in the library</span>
          <button onClick={onClose} style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer' }}><X size={15} color="var(--gray-500)" /></button>
        </div>
        <div style={{ padding: 14 }}>
          <p style={{ fontSize: 12.5, color: 'var(--gray-500)', lineHeight: 1.6, marginTop: 0 }}>
            This searches the existing library for anything matching this description, then actually
            looks at each one with Claude before proposing it. Nothing is changed — you review the
            matches and approve them.
          </p>

          <div style={{ marginBottom: 12 }}>
            <div style={{ ...label, marginBottom: 4 }}>Group name</div>
            <input style={input} value={sweepName} onChange={(e) => setSweepName(e.target.value)} />
          </div>
          <div style={{ marginBottom: 12 }}>
            <div style={{ ...label, marginBottom: 4 }}>What counts as a match</div>
            <textarea style={{ ...input, minHeight: 80, resize: 'vertical' }} value={brief} onChange={(e) => setBrief(e.target.value)} />
          </div>
          <div style={{ marginBottom: 12 }}>
            <div style={{ ...label, marginBottom: 4 }}>Tags to apply to approved matches</div>
            <TagInput tags={tags} setTags={setTags} />
          </div>
          <div style={{ marginBottom: 14 }}>
            <div style={{ ...label, marginBottom: 4 }}>How many to scan</div>
            <select style={input} value={cap} onChange={(e) => setCap(parseInt(e.target.value, 10))}>
              <option value={150}>Up to 150 (quick)</option>
              <option value={400}>Up to 400 (recommended)</option>
              <option value={800}>Up to 800 (thorough, slower)</option>
            </select>
          </div>

          {err ? <div style={{ fontSize: 12, color: '#c33', marginBottom: 10 }}>{err}</div> : null}
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={go} disabled={busy} style={{ ...btnPrimary, flex: 1, opacity: busy ? 0.6 : 1 }}>
              {busy ? 'Starting…' : 'Start the sweep'}
            </button>
            <button onClick={onClose} style={btnGhost}>Cancel</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Sweeps tab ───────────────────────────────────────────────────────────────
function SweepsTab({ user, jumpTo, onConsumeJump }) {
  const [sweeps, setSweeps] = useState([]);
  const [openId, setOpenId] = useState(null);
  const [newSweep, setNewSweep] = useState(false);

  const load = useCallback(async () => {
    try { setSweeps(await neonQuery('media_sweeps_list', { limit: 25 }) || []); } catch (e) { /* keep last */ }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (jumpTo) { setOpenId(jumpTo); onConsumeJump(); } }, [jumpTo, onConsumeJump]);

  // Poll while anything is still running so the progress line actually moves.
  const running = sweeps.some((s) => s.status === 'queued' || s.status === 'scanning');
  useEffect(() => {
    if (!running) return undefined;
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [running, load]);

  if (openId) return <SweepDetail sweepId={openId} user={user} onBack={() => { setOpenId(null); load(); }} />;

  const startButton = (
    <button onClick={() => setNewSweep(true)} style={{ ...btnPrimary, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <Sparkles size={13} /> Start a sweep
    </button>
  );

  return (
    <div>
      <div style={{ ...card, padding: 14, marginBottom: 14, background: '#fafbfc', display: 'flex', alignItems: 'flex-start', gap: 10 }}>
        <Sparkles size={17} color="var(--navy)" style={{ marginTop: 2, flexShrink: 0 }} />
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 600, color: 'var(--navy)', fontSize: 14, marginBottom: 4 }}>Tidy up what's already in the library</div>
          <div style={{ fontSize: 12.5, color: 'var(--gray-500)', lineHeight: 1.6 }}>
            Describe something, and this goes looking through the library for it, actually looking at each
            photo rather than trusting the labels. You review the matches before anything is tagged.
            Nothing needs uploading first.
          </div>
        </div>
        <div style={{ flexShrink: 0 }}>{startButton}</div>
      </div>

      {!sweeps.length ? (
        <Empty
          icon={<Sparkles size={26} color="var(--gray-500)" />}
          title="No sweeps yet"
          body="Start one above. The quickest way in is to pick a term you've already defined in Vernacular — it fills in the description for you."
        />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {sweeps.map((s) => (
        <button key={s.id} onClick={() => setOpenId(s.id)} style={{ ...card, padding: 12, textAlign: 'left', cursor: 'pointer', width: '100%' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, flexWrap: 'wrap' }}>
            <span style={{ fontWeight: 600, color: 'var(--navy)', fontSize: 14 }}>{s.label}</span>
            <SweepStatus sweep={s} />
            <span style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--gray-500)' }}>
              {s.status === 'scanning' ? `${s.scanned} of ${s.pool} checked · ${s.matched} matched`
                : s.status === 'applied' ? `${s.applied_count} tagged`
                : `${s.matched} match${s.matched === 1 ? '' : 'es'} from ${s.pool} scanned`}
            </span>
          </div>
          <TagChips tags={s.apply_tags} />
          {s.error ? <div style={{ fontSize: 11.5, color: '#7a5200', marginTop: 6, lineHeight: 1.5 }}>{s.error}</div> : null}
            </button>
          ))}
        </div>
      )}

      {newSweep ? (
        <NewSweepDialog
          user={user}
          onClose={() => setNewSweep(false)}
          onStarted={(id) => { setNewSweep(false); load(); setOpenId(id); }}
        />
      ) : null}
    </div>
  );
}

// Start a sweep without staging anything first. The fastest route in is to pick
// a term already defined in Vernacular: its definition and visual cues are
// exactly the description a sweep needs, so the dictionary does double duty
// instead of asking someone to write the same thing twice.
function NewSweepDialog({ user, onClose, onStarted }) {
  const [terms, setTerms] = useState([]);
  const [pickedTerm, setPickedTerm] = useState('');
  const [sweepLabel, setSweepLabel] = useState('');
  const [brief, setBrief] = useState('');
  const [cues, setCues] = useState('');
  const [tags, setTags] = useState([]);
  const [brand, setBrand] = useState('');
  const [cap, setCap] = useState(400);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    neonQuery('media_vocab_list', {}).then((rows) => setTerms(rows || [])).catch(() => setTerms([]));
  }, []);

  const applyTerm = (term) => {
    setPickedTerm(term);
    const v = terms.find((t) => t.term === term);
    if (!v) return;
    setSweepLabel(v.label || '');
    setBrief(v.definition || '');
    setCues(v.visual_cues || '');
    setTags([v.term]);
  };

  const go = async () => {
    if (!sweepLabel.trim()) { setErr('Give it a name.'); return; }
    if (!tags.length) { setErr('Pick at least one tag to apply to the matches.'); return; }
    setBusy(true); setErr('');
    try {
      const r = await intakePost({
        action: 'start-sweep', label: sweepLabel, brief, visualCues: cues, applyTags: tags,
        vocabTerm: pickedTerm || null, brand: brand || null, poolCap: cap, admin_id: user?.id,
      });
      onStarted(r.sweep.id);
    } catch (e) { setErr(e.message); setBusy(false); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 900, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div style={{ ...card, width: 'min(600px, 100%)', maxHeight: '92vh', overflowY: 'auto' }}>
        <div style={{ padding: '12px 14px', borderBottom: '1px solid var(--gray-200)', display: 'flex', alignItems: 'center', gap: 8 }}>
          <Sparkles size={15} color="var(--orange)" />
          <span style={{ fontWeight: 600, color: 'var(--navy)', fontSize: 14 }}>Start a sweep</span>
          <button onClick={onClose} style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer' }}><X size={15} color="var(--gray-500)" /></button>
        </div>
        <div style={{ padding: 14 }}>
          <div style={{ marginBottom: 14, padding: 10, background: '#fafbfc', border: '1px solid var(--gray-200)', borderRadius: 6 }}>
            <div style={{ ...label, marginBottom: 4 }}>Start from a term you've already defined</div>
            <select style={input} value={pickedTerm} onChange={(e) => applyTerm(e.target.value)}>
              <option value="">— or describe it yourself below —</option>
              {terms.map((t) => (
                <option key={t.term} value={t.term}>{t.label} ({t.asset_count} tagged so far)</option>
              ))}
            </select>
            <div style={{ fontSize: 11, color: 'var(--gray-500)', marginTop: 5, lineHeight: 1.5 }}>
              Picking one fills in everything below from the Vernacular tab. Edit it here if you want.
            </div>
          </div>

          <div style={{ marginBottom: 12 }}>
            <div style={{ ...label, marginBottom: 4 }}>What are you looking for</div>
            <input style={input} value={sweepLabel} onChange={(e) => setSweepLabel(e.target.value)} placeholder="e.g. Official logo" />
          </div>
          <div style={{ marginBottom: 12 }}>
            <div style={{ ...label, marginBottom: 4 }}>What counts as a match</div>
            <textarea style={{ ...input, minHeight: 76, resize: 'vertical' }} value={brief} onChange={(e) => setBrief(e.target.value)}
              placeholder="Plain English, and say what it is NOT. That part does most of the work." />
          </div>
          <div style={{ marginBottom: 12 }}>
            <div style={{ ...label, marginBottom: 4 }}>What it looks like <span style={{ textTransform: 'none', fontWeight: 400 }}>— visual cues only</span></div>
            <textarea style={{ ...input, minHeight: 64, resize: 'vertical' }} value={cues} onChange={(e) => setCues(e.target.value)}
              placeholder="Shapes, colours, setting, framing. This is what it matches on." />
          </div>
          <div style={{ marginBottom: 12 }}>
            <div style={{ ...label, marginBottom: 4 }}>Tag to apply to approved matches</div>
            <TagInput tags={tags} setTags={setTags} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 14 }}>
            <div>
              <div style={{ ...label, marginBottom: 4 }}>Brand</div>
              <select style={input} value={brand} onChange={(e) => setBrand(e.target.value)}>
                <option value="">Both</option>
                {BRANDS.map((b) => <option key={b.id} value={b.id}>{b.label}</option>)}
              </select>
            </div>
            <div>
              <div style={{ ...label, marginBottom: 4 }}>How many to check</div>
              <select style={input} value={cap} onChange={(e) => setCap(parseInt(e.target.value, 10))}>
                <option value={150}>Up to 150 (quick)</option>
                <option value={400}>Up to 400 (recommended)</option>
                <option value={800}>Up to 800 (thorough)</option>
                <option value={1200}>Up to 1200 (everything)</option>
              </select>
            </div>
          </div>
          <div style={{ fontSize: 11.5, color: 'var(--gray-500)', lineHeight: 1.5, marginBottom: 12 }}>
            It looks at one image per check, most-likely first, so a bigger number costs more and finds
            a little more. 400 is the sensible default.
          </div>

          {err ? <div style={{ fontSize: 12, color: '#c33', marginBottom: 10 }}>{err}</div> : null}
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={go} disabled={busy} style={{ ...btnPrimary, flex: 1, opacity: busy ? 0.6 : 1 }}>
              {busy ? 'Starting…' : 'Start the sweep'}
            </button>
            <button onClick={onClose} style={btnGhost}>Cancel</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function SweepStatus({ sweep }) {
  const map = {
    queued: { text: 'Queued', bg: '#eef1f4', fg: 'var(--gray-500)' },
    scanning: { text: 'Scanning', bg: '#fff4e6', fg: '#7a3500' },
    review: { text: 'Needs review', bg: '#e6f0fa', fg: 'var(--navy)' },
    applied: { text: 'Applied', bg: '#e8f5ec', fg: '#1a7f37' },
    error: { text: 'Error', bg: '#fdecea', fg: '#c33' },
  };
  const s = map[sweep.status] || map.queued;
  return (
    <span style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, padding: '2px 6px', borderRadius: 3, background: s.bg, color: s.fg }}>
      {s.text}
    </span>
  );
}

function SweepDetail({ sweepId, user, onBack }) {
  const [sweep, setSweep] = useState(null);
  const [cands, setCands] = useState([]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');

  const load = useCallback(async () => {
    try {
      const rows = await neonQuery('media_sweep_get', { id: sweepId });
      if (rows && rows[0]) {
        setSweep(rows[0].sweep);
        setCands((rows[0].candidates || []).map((c) => ({
          // High confidence is pre-approved; medium and low start unchecked so a
          // reviewer opts IN to the uncertain ones rather than having to catch them.
          ...c, decision: c.decision === 'pending' ? (c.confidence === 'high' ? 'approved' : 'rejected') : c.decision,
        })));
      }
    } catch (e) { setErr(e.message); }
  }, [sweepId]);

  useEffect(() => { load(); }, [load]);

  const stillRunning = sweep && (sweep.status === 'queued' || sweep.status === 'scanning');
  useEffect(() => {
    if (!stillRunning) return undefined;
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [stillRunning, load]);

  const toggle = (id) => setCands((prev) => prev.map((c) => (c.id === id ? { ...c, decision: c.decision === 'approved' ? 'rejected' : 'approved' } : c)));
  const setAll = (decision) => setCands((prev) => prev.map((c) => (c.applied_at ? c : { ...c, decision })));

  const apply = async () => {
    const approved = cands.filter((c) => c.decision === 'approved' && !c.applied_at).map((c) => c.id);
    const rejected = cands.filter((c) => c.decision !== 'approved' && !c.applied_at).map((c) => c.id);
    if (!approved.length) { setErr('Nothing is ticked.'); return; }
    setBusy(true); setErr(''); setMsg('');
    try {
      if (rejected.length) await neonQuery('media_sweep_decide', { ids: rejected, decision: 'rejected' });
      await neonQuery('media_sweep_decide', { ids: approved, decision: 'approved' });
      const rows = await neonQuery('media_sweep_apply', { id: sweepId, admin_id: user?.id });
      const r = (rows && rows[0]) || {};
      setMsg(`Tagged ${r.changed || 0} asset${r.changed === 1 ? '' : 's'} in the library.`);
      await load();
    } catch (e) { setErr(e.message); }
    setBusy(false);
  };

  if (!sweep) return <div style={{ ...card, padding: 20, fontSize: 13, color: 'var(--gray-500)' }}>Loading…</div>;

  const pickedCount = cands.filter((c) => c.decision === 'approved' && !c.applied_at).length;
  const done = sweep.status === 'applied';

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
        <button onClick={onBack} style={{ ...btnGhost, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <ChevronLeft size={13} /> All sweeps
        </button>
        <button
          onClick={async () => {
            if (!window.confirm('Discard this sweep and its proposals? Any tags already applied stay applied.')) return;
            try { await neonQuery('media_sweep_delete', { id: sweepId }); onBack(); }
            catch (e) { setErr(e.message); }
          }}
          style={{ ...btnGhost, marginLeft: 'auto', color: '#c33', borderColor: '#f0c4c2', display: 'inline-flex', alignItems: 'center', gap: 6 }}
        >
          <Trash2 size={13} /> Discard sweep
        </button>
      </div>

      <div style={{ ...card, padding: 14, marginBottom: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <h2 style={{ fontSize: 17, color: 'var(--navy)', margin: 0 }}>{sweep.label}</h2>
          <SweepStatus sweep={sweep} />
          <span style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--gray-500)' }}>
            {stillRunning ? `Checked ${sweep.scanned} of ${sweep.pool}…` : `Looked at ${sweep.scanned} candidates, found ${sweep.matched}`}
          </span>
        </div>
        {sweep.brief ? <div style={{ fontSize: 12.5, color: 'var(--gray-500)', lineHeight: 1.6, marginTop: 6 }}>{sweep.brief}</div> : null}
        <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 11, color: 'var(--gray-500)' }}>Will add:</span>
          <TagChips tags={sweep.apply_tags} />
        </div>
      </div>

      {msg ? <div style={{ ...card, padding: '10px 12px', marginBottom: 12, background: '#e8f5ec', borderColor: '#b7e0c2', color: '#1a7f37', fontSize: 13 }}>{msg}</div> : null}
      {err ? <div style={{ ...card, padding: '10px 12px', marginBottom: 12, background: '#fdecea', borderColor: '#f5c2c0', color: '#c33', fontSize: 13 }}>{err}</div> : null}
      {sweep.error ? <div style={{ ...card, padding: '10px 12px', marginBottom: 12, background: '#fff8e6', borderColor: '#ffd98a', color: '#7a5200', fontSize: 13 }}>{sweep.error}</div> : null}

      {stillRunning ? (
        <div style={{ ...card, padding: 20, fontSize: 13, color: 'var(--gray-500)', display: 'flex', alignItems: 'center', gap: 8 }}>
          <Loader2 size={15} style={{ animation: 'spin 1s linear infinite' }} /> Looking through the library. This keeps running if you leave the page.
        </div>
      ) : !cands.length ? (
        // "0 matches" means two very different things. Don't report a broken run
        // as a confident verdict that the library has nothing.
        /could not be checked/.test(sweep.error || '') ? (
          <Empty
            icon={<AlertTriangle size={26} color="#c33" />}
            title="This run couldn't check anything"
            body="The images were never actually looked at, so this is not a verdict on the library. See the message above, then discard this sweep and run it again."
          />
        ) : (
          <Empty
            icon={<Search size={26} color="var(--gray-500)" />}
            title="No matches found"
            body="Everything it looked at was rejected. Try a sweep with a few more describing words in the brief, or a wider scan."
          />
        )
      ) : (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--navy)' }}>
              {cands.length} proposed · {pickedCount} ticked
            </div>
            {!done ? (
              <>
                <button onClick={() => setAll('approved')} style={{ ...btnGhost, padding: '5px 10px', fontSize: 12 }}>Tick all</button>
                <button onClick={() => setAll('rejected')} style={{ ...btnGhost, padding: '5px 10px', fontSize: 12 }}>Untick all</button>
                <button onClick={apply} disabled={busy || !pickedCount} style={{ ...btnPrimary, marginLeft: 'auto', opacity: busy || !pickedCount ? 0.5 : 1, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  <Check size={13} /> Tag {pickedCount} asset{pickedCount === 1 ? '' : 's'}
                </button>
              </>
            ) : null}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 10 }}>
            {cands.map((c) => {
              const picked = c.decision === 'approved';
              return (
                <div
                  key={c.id}
                  onClick={() => (!done && !c.applied_at ? toggle(c.id) : null)}
                  style={{
                    ...card, overflow: 'hidden', cursor: done || c.applied_at ? 'default' : 'pointer',
                    borderColor: picked ? 'var(--orange)' : 'var(--gray-200)', borderWidth: picked ? 2 : 1,
                    opacity: picked ? 1 : 0.62,
                  }}
                >
                  <div style={{ position: 'relative', background: '#f4f4f6', aspectRatio: '4 / 3' }}>
                    <img src={thumbUrl(c.r2_thumb_key)} alt={c.suggested_name || ''} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                    <span style={{
                      position: 'absolute', top: 6, left: 6, width: 18, height: 18, borderRadius: 3,
                      background: picked ? 'var(--orange)' : 'rgba(255,255,255,0.9)',
                      border: `1px solid ${picked ? 'var(--orange)' : 'var(--gray-200)'}`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      {picked ? <Check size={12} color="#fff" /> : null}
                    </span>
                    <span style={{
                      position: 'absolute', top: 6, right: 6, fontSize: 9, fontWeight: 700, textTransform: 'uppercase',
                      letterSpacing: 0.4, padding: '2px 5px', borderRadius: 3,
                      background: c.confidence === 'high' ? 'rgba(26,127,55,0.9)' : 'rgba(122,82,0,0.85)', color: '#fff',
                    }}>{c.confidence}</span>
                  </div>
                  <div style={{ padding: 9 }}>
                    <div style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--navy)', wordBreak: 'break-word' }}>{c.suggested_name || c.original_name}</div>
                    <div style={{ fontSize: 10.5, color: 'var(--gray-500)', margin: '2px 0 5px' }}>{c.category}/{c.subcategory}</div>
                    {c.reason ? <div style={{ fontSize: 11, color: 'var(--gray-500)', lineHeight: 1.45, fontStyle: 'italic' }}>{c.reason}</div> : null}
                    {c.applied_at ? <div style={{ fontSize: 10.5, color: '#1a7f37', marginTop: 5, fontWeight: 600 }}>Tagged</div> : null}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

// ── Vernacular tab ───────────────────────────────────────────────────────────
function VernacularTab({ user, seed, onConsumeSeed }) {
  const [terms, setTerms] = useState([]);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try { setTerms(await neonQuery('media_vocab_list', { includeInactive: true }) || []); } catch (e) { setTerms([]); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!seed) return;
    setEditing({
      term: normalizeTag(seed.term || ''), label: seed.label || '', definition: seed.definition || '',
      visual_cues: '', aliases: [], category: seed.category || '', subcategory: seed.subcategory || '',
      teach_ai: true, is_active: true, sort_order: 100,
    });
    onConsumeSeed();
  }, [seed, onConsumeSeed]);

  const blank = {
    term: '', label: '', definition: '', visual_cues: '', aliases: [],
    category: '', subcategory: '', teach_ai: true, is_active: true, sort_order: 100,
  };

  return (
    <div>
      <div style={{ ...card, padding: 14, marginBottom: 14, background: '#fafbfc' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
          <BookOpen size={17} color="var(--navy)" style={{ marginTop: 2, flexShrink: 0 }} />
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 600, color: 'var(--navy)', fontSize: 14, marginBottom: 4 }}>What we call things</div>
            <div style={{ fontSize: 12.5, color: 'var(--gray-500)', lineHeight: 1.6 }}>
              This is the house dictionary, and it does three jobs at once. It reminds us what we call
              things, it teaches Claude our words so new uploads get tagged in them, and it makes those
              words work in the Media Library search box. Define a term once and all three improve.
            </div>
          </div>
          <button onClick={() => setEditing(blank)} style={{ ...btnPrimary, display: 'inline-flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
            <Plus size={13} /> Add a term
          </button>
        </div>
      </div>

      {loading ? (
        <div style={{ ...card, padding: 20, fontSize: 13, color: 'var(--gray-500)' }}>Loading…</div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(330px, 1fr))', gap: 10 }}>
          {terms.map((t) => (
            <div key={t.id} style={{ ...card, padding: 12, opacity: t.is_active ? 1 : 0.55 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 5, flexWrap: 'wrap' }}>
                <span style={{ fontWeight: 600, color: 'var(--navy)', fontSize: 14 }}>{t.label}</span>
                <code style={{ fontSize: 11, background: '#eef4fa', color: 'var(--navy)', padding: '1px 5px', borderRadius: 3 }}>{t.term}</code>
                {!t.is_active ? <span style={{ fontSize: 10, color: 'var(--gray-500)' }}>(off)</span> : null}
                <button onClick={() => setEditing(t)} style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', padding: 2 }}>
                  <Edit2 size={13} color="var(--gray-500)" />
                </button>
              </div>
              {t.definition ? <div style={{ fontSize: 12, color: 'var(--gray-500)', lineHeight: 1.55, marginBottom: 6 }}>{t.definition}</div> : null}
              {t.visual_cues ? (
                <div style={{ fontSize: 11.5, color: 'var(--gray-500)', lineHeight: 1.5, marginBottom: 6, paddingLeft: 8, borderLeft: '2px solid var(--gray-200)' }}>
                  <strong style={{ color: 'var(--navy)' }}>Looks like:</strong> {t.visual_cues}
                </div>
              ) : null}
              {(t.aliases || []).length ? (
                <div style={{ fontSize: 11, color: 'var(--gray-500)', marginBottom: 6 }}>
                  <strong style={{ color: 'var(--navy)' }}>Also search:</strong> {t.aliases.join(' · ')}
                </div>
              ) : null}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 11, color: 'var(--gray-500)', marginTop: 8, borderTop: '1px solid #f1f1f3', paddingTop: 7 }}>
                <span><strong style={{ color: 'var(--navy)' }}>{t.asset_count}</strong> tagged</span>
                {t.teach_ai ? <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}><Sparkles size={11} /> teaches Claude</span> : null}
              </div>
            </div>
          ))}
        </div>
      )}

      {editing ? (
        <VocabEditor
          value={editing}
          user={user}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      ) : null}
    </div>
  );
}

function VocabEditor({ value, user, onClose, onSaved }) {
  const [v, setV] = useState({ ...value, aliases: value.aliases || [] });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const set = (k, val) => setV((p) => ({ ...p, [k]: val }));

  const save = async () => {
    if (!v.term.trim() || !v.label.trim()) { setErr('A term and a name are both required.'); return; }
    setBusy(true); setErr('');
    try {
      await neonQuery('media_vocab_upsert', { ...v, id: v.id || null, admin_id: user?.id });
      onSaved();
    } catch (e) { setErr(e.message); setBusy(false); }
  };

  const remove = async () => {
    if (!v.id) return;
    if (!window.confirm(`Delete "${v.label}"? Assets already tagged ${v.term} keep the tag — this only removes the definition.`)) return;
    setBusy(true);
    try { await neonQuery('media_vocab_delete', { id: v.id }); onSaved(); }
    catch (e) { setErr(e.message); setBusy(false); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 900, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div style={{ ...card, width: 'min(620px, 100%)', maxHeight: '92vh', overflowY: 'auto' }}>
        <div style={{ padding: '12px 14px', borderBottom: '1px solid var(--gray-200)', display: 'flex', alignItems: 'center', gap: 8 }}>
          <BookOpen size={15} color="var(--navy)" />
          <span style={{ fontWeight: 600, color: 'var(--navy)', fontSize: 14 }}>{v.id ? 'Edit term' : 'New term'}</span>
          <button onClick={onClose} style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer' }}><X size={15} color="var(--gray-500)" /></button>
        </div>

        <div style={{ padding: 14 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}>
            <div>
              <div style={{ ...label, marginBottom: 4 }}>Name we say</div>
              <input style={input} value={v.label} onChange={(e) => set('label', e.target.value)} placeholder="Official logo" />
            </div>
            <div>
              <div style={{ ...label, marginBottom: 4 }}>Tag written on assets</div>
              <input style={input} value={v.term} onChange={(e) => set('term', normalizeTag(e.target.value))} placeholder="official-logo" />
            </div>
          </div>

          <div style={{ marginBottom: 12 }}>
            <div style={{ ...label, marginBottom: 4 }}>What we mean by it <span style={{ textTransform: 'none', fontWeight: 400 }}>— for people</span></div>
            <textarea style={{ ...input, minHeight: 76, resize: 'vertical' }} value={v.definition || ''} onChange={(e) => set('definition', e.target.value)} placeholder="Say what it is, and what it is NOT. The 'not' is usually what settles arguments." />
          </div>

          <div style={{ marginBottom: 12 }}>
            <div style={{ ...label, marginBottom: 4 }}>What it looks like <span style={{ textTransform: 'none', fontWeight: 400 }}>— for Claude</span></div>
            <textarea style={{ ...input, minHeight: 76, resize: 'vertical' }} value={v.visual_cues || ''} onChange={(e) => set('visual_cues', e.target.value)} placeholder="Describe only what is visible in the picture: shapes, colours, setting, framing. This is what the AI matches on." />
            <div style={{ fontSize: 11, color: 'var(--gray-500)', marginTop: 4, lineHeight: 1.5 }}>
              Visual cues only. &ldquo;Flat artwork on a plain background, no photograph&rdquo; works; &ldquo;our brand mark&rdquo; does not.
            </div>
          </div>

          <div style={{ marginBottom: 12 }}>
            <div style={{ ...label, marginBottom: 4 }}>Other ways people search for it</div>
            <AliasInput aliases={v.aliases} setAliases={(a) => set('aliases', a)} />
            <div style={{ fontSize: 11, color: 'var(--gray-500)', marginTop: 4, lineHeight: 1.5 }}>
              Typing any of these in the Media Library jumps this group to the top of the results.
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}>
            <div>
              <div style={{ ...label, marginBottom: 4 }}>Usual category</div>
              <select style={input} value={v.category || ''} onChange={(e) => set('category', e.target.value || null)}>
                <option value="">— any —</option>
                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <div style={{ ...label, marginBottom: 4 }}>Usual subcategory</div>
              <input style={input} value={v.subcategory || ''} onChange={(e) => set('subcategory', e.target.value || null)} placeholder="logo" />
            </div>
          </div>

          <div style={{ display: 'flex', gap: 16, marginBottom: 14, flexWrap: 'wrap' }}>
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12.5, color: 'var(--navy)', cursor: 'pointer' }}>
              <input type="checkbox" checked={v.teach_ai !== false} onChange={(e) => set('teach_ai', e.target.checked)} />
              Teach this to Claude on upload
            </label>
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12.5, color: 'var(--navy)', cursor: 'pointer' }}>
              <input type="checkbox" checked={v.is_active !== false} onChange={(e) => set('is_active', e.target.checked)} />
              Active
            </label>
          </div>

          {err ? <div style={{ fontSize: 12, color: '#c33', marginBottom: 10 }}>{err}</div> : null}
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button onClick={save} disabled={busy} style={{ ...btnPrimary, opacity: busy ? 0.6 : 1, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <Save size={13} /> {busy ? 'Saving…' : 'Save term'}
            </button>
            <button onClick={onClose} style={btnGhost}>Cancel</button>
            {v.id ? (
              <button onClick={remove} style={{ ...btnGhost, marginLeft: 'auto', color: '#c33', borderColor: '#f0c4c2', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <Trash2 size={13} /> Delete
              </button>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}

// Aliases are natural PHRASES, not slugs — "official logo" must stay two words,
// so this deliberately does not run them through normalizeTag.
function AliasInput({ aliases, setAliases }) {
  const [text, setText] = useState('');
  const commit = () => {
    const t = text.trim().toLowerCase().replace(/\s+/g, ' ');
    setText('');
    if (t && !aliases.includes(t)) setAliases([...aliases, t]);
  };
  return (
    <div>
      <div style={{ display: 'flex', gap: 6 }}>
        <input
          style={input} value={text} placeholder="e.g. real logo, logo file, brand mark"
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); commit(); } }}
          onBlur={commit}
        />
        <button type="button" onClick={commit} style={{ ...btnPrimary, padding: '7px 10px' }}><Plus size={13} /></button>
      </div>
      {aliases.length ? (
        <div style={{ marginTop: 6, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
          {aliases.map((a) => (
            <span key={a} style={{ background: '#eef4fa', color: 'var(--navy)', border: '1px solid #cfe0ef', padding: '2px 4px 2px 8px', borderRadius: 3, fontSize: 11, display: 'inline-flex', alignItems: 'center', gap: 2 }}>
              {a}
              <button onClick={() => setAliases(aliases.filter((x) => x !== a))} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 1, display: 'inline-flex', color: 'inherit' }}>
                <X size={11} />
              </button>
            </span>
          ))}
        </div>
      ) : null}
    </div>
  );
}
